import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, Mail, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/utils2";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6 noise-overlay">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <div className="h-11 w-11 grid place-items-center bg-orange-600 rounded-sm">
            <Wrench className="h-6 w-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black text-2xl tracking-tighter">
            MAINTAIN<span className="text-orange-500">HQ</span>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// ACCESS RECOVERY</div>
        <h2 className="font-display font-black text-3xl tracking-tight mb-3">
          {sent ? "Check your email." : "Forgot your password?"}
        </h2>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          {sent
            ? "If an account exists for that email, a reset link is on its way. The link works once and expires in 30 minutes."
            : "No worries — enter your email and we'll send you a secure link to set a new one."}
        </p>

        {sent ? (
          <div className="border border-orange-900/50 bg-orange-950/30 p-4 rounded-sm flex items-start gap-3 mb-6">
            <Mail className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
            <div className="text-sm text-orange-100">
              Reset link sent to <span className="font-bold text-white">{email}</span>. Can't find it? Check your spam folder.
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="fp-email" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Email
              </Label>
              <Input
                id="fp-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                data-testid="forgot-email-input"
                placeholder="operator@shop.com"
              />
            </div>
            {error ? (
              <div className="border border-red-900/50 bg-red-950/40 text-red-300 px-3 py-2 text-sm rounded-sm" data-testid="forgot-error">
                {error}
              </div>
            ) : null}
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-orange-600 hover:bg-orange-500 text-white uppercase tracking-widest font-bold rounded-sm py-6"
              data-testid="forgot-submit-button"
            >
              {busy ? "Sending..." : "Send reset link →"}
            </Button>
          </form>
        )}

        <div className="mt-8 text-center">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-orange-500 uppercase tracking-widest">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
